import { defineStore } from 'pinia'

import { Order, OrderBy } from '@shared/models/enums/order'
import { StoreState } from '@shared/models/interfaces/store'
import { ILocalizedEntityType } from '@shared/models/proto/base/localized-entity'
import { Nullable } from '@shared/models/types/primary'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { getDataToSet } from '@shared/utils/store/data-to-set'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { ChatWidget } from '@protos/apps/vc/chat_widget_pb'
import { ChatWidgetMessageType } from '@protos/chat_widget_message_type_pb'
import { PersonType } from '@protos/person_type_pb'

import { getWelcomeMessage } from '@vc/helpers/plus-chat-messages'
import { IChatWidget } from '@vc/models/proto/base/chat-widgets'
import {
  ChatWidgetShowDTO,
  ChatWidgetsIndexDTO,
  GetTranslationServicesDTO,
} from '@vc/models/proto/dto/chat-widgets'
import {
  ChatWidgetIndexRO,
  ChatWidgetItemRO,
  TranslationServicesRO,
} from '@vc/models/proto/ro/chat-widgets'

interface State {
  widget: StoreState<ChatWidget>
  widgets: StoreState<ChatWidgetIndexRO>
  translationServices: StoreState<TranslationServicesRO>
}

const state = (): State => ({
  widget: getInitialState(),
  widgets: getInitialState(),
  translationServices: getInitialState(),
})

export const apiTransport = new ApiTransport(resourcePath, 'vc')

export const useChatWidgetsStore = defineStore('chat-widgets', {
  state,

  actions: {
    async fetchWidgets(dto: {
      page?: number
      perPage?: number
      orderBy?: string
      order?: string
      nameFilter?: string
    }) {
      this.widgets = updateState(this.widgets)

      try {
        const { data, meta } = (await apiTransport.get<
          ChatWidgetsIndexDTO,
          ChatWidgetIndexRO
        >(
          '/chat_widgets',
          new ChatWidgetsIndexDTO(dto),
          ChatWidgetIndexRO
        )) ?? { data: [], meta: {} }

        const dataToSet = getDataToSet(
          meta?.page ?? 1,
          data,
          this.widgets.data?.data
        )

        this.widgets = updateState(
          this.widgets,
          new ChatWidgetIndexRO({ meta, data: dataToSet })
        )

        return { meta, data: dataToSet }
      } catch (e) {
        this.widgets = updateState(this.widgets, e as Error)
        throw e
      }
    },

    async fetchWidgetById(dto: { chatWidgetId: number }) {
      this.widget = updateState(this.widget)

      try {
        const { data } = (await apiTransport.get<
          ChatWidgetShowDTO,
          ChatWidgetItemRO
        >(
          `/chat_widgets/${dto.chatWidgetId}`,
          new ChatWidgetShowDTO(dto),
          ChatWidgetItemRO
        )) ?? { data: {} }

        this.widget = updateState(this.widget, data)
        return data
      } catch (e) {
        this.widget = updateState(this.widget, e as Error)
        throw e
      }
    },

    async fetchNewestWidget() {
      const dto = {
        page: 1,
        perPage: 1,
        orderBy: OrderBy.CreatedAt,
        order: Order.Descending,
      }

      const { data } = (await apiTransport.get<
        ChatWidgetsIndexDTO,
        ChatWidgetIndexRO
      >('/chat_widgets', new ChatWidgetsIndexDTO(dto), ChatWidgetIndexRO)) ?? {
        data: [],
        meta: {},
      }

      if (!data.length) {
        return
      }

      const widget = await this.fetchWidgetById({ chatWidgetId: data[0].id })

      return widget ? widget : null
    },

    async fetchTranslationServices(dto: { chatWidgetId: number }) {
      this.translationServices = updateState(this.translationServices)

      try {
        const data = await apiTransport.get<
          GetTranslationServicesDTO,
          TranslationServicesRO
        >(
          '/chat_widgets/translation_services',
          new GetTranslationServicesDTO(dto),
          TranslationServicesRO
        )

        this.translationServices = updateState(this.translationServices, data)
      } catch (error) {
        this.translationServices = updateState(
          this.translationServices,
          new Error(`${error}`)
        )
      }
    },

    setDefaultWidget() {
      this.widget = getInitialState()
    },
  },

  getters: {
    currentWidgetPersonType: (state) =>
      state.widget.data?.personType ?? PersonType.PERSON_PATIENT,
    currentWidget: (state): Nullable<IChatWidget> => state.widget.data,
    isBotRuleAssigned: (state) =>
      state.widget.data?.conversationRule?.isBot ?? false,
    isSendingMms: (state) => state.widget.data?.isSendingMms ?? false,
    welcomeMessageTexts: (state): ILocalizedEntityType[] =>
      state.widget?.data?.messages.find(
        (item) =>
          item.messageType === ChatWidgetMessageType.WELCOME && item.isDisplay
      )?.localizedTexts ?? [],

    welcomeMessageEntity: (state) => {
      const texts: ILocalizedEntityType[] =
        state.widget?.data?.messages.find(
          (item) =>
            item.messageType === ChatWidgetMessageType.WELCOME && item.isDisplay
        )?.localizedTexts ?? []

      return getWelcomeMessage(texts)
    },
    patientGroupsOptions: (state) =>
      (state.widget.data?.patientGroups ?? []).map((item) => ({
        label: item.name,
        value: item.id,
      })),
  },
})
